import { mobileApiRequest } from '../../apiRequests'

/**
 * Send team invitation to join account
 * @param {string} accountID - Account ID to invite user to
 * @param {string} email - Email address to invite
 * @param {string} role - Account role to assign (owner, admin, member)
 * @returns {Promise<Object>} Returns { invitation: { invitationID, invitedEmail, status, expiresDate }, messages }
 */
export const sendTeamInvitation = async (accountID, email, role) => {
    const action = {
        action: 'inviteTeamMember',
        accountID,
        email: email.toLowerCase().trim(),
        role,
    }
    const response = await mobileApiRequest(action)
    if (response?.invitation) {
        return response
    }
    throw new Error(response?.error || 'Failed to send invitation')
}

/**
 * Validate team invitation (public endpoint - no auth required)
 * @param {string} invitationToken - Invitation token from URL
 * @param {string} invitationID - Optional invitation ID (alternative to token)
 * @returns {Promise<Object>} Returns { invitation: { valid, invitationID, accountID, accountName, email, role, inviterName, inviterEmail, createdAt, expiresAt, invitationStatus } }
 */
export const validateTeamInvitation = async (invitationToken, invitationID) => {
    const action = {
        action: 'validateTeamInvitation',
    }

    if (invitationToken) {
        action.invitationToken = invitationToken
    } else if (invitationID) {
        action.invitationID = invitationID
    }

    const response = await mobileApiRequest(action)

    // Handle error responses from guide
    if (response?.error) {
        const error = new Error(response.errorMessage || response.error)
        error.code = response.error
        error.body = response.body
        throw error
    }

    if (response?.invitation) {
        return response
    }

    throw new Error('Failed to validate invitation')
}

/**
 * Accept team invitation
 * @param {string} invitationToken - Invitation token from URL
 * @returns {Promise<Object>} Returns { messages }
 */
export const acceptTeamInvitation = async (invitationToken) => {
    const action = {
        action: 'acceptTeamInvitation',
        invitationToken,
    }
    const response = await mobileApiRequest(action)
    if (response?.messages) {
        return response
    }
    throw new Error(response?.error || 'Failed to accept invitation')
}

/**
 * Cancel team invitation
 * @param {string} invitationID - Invitation ID to cancel
 * @returns {Promise<Object>} Returns { messages }
 */
export const cancelTeamInvitation = async (invitationID) => {
    const action = {
        action: 'cancelTeamInvitation',
        invitationID,
    }
    const response = await mobileApiRequest(action)
    if (response?.messages) {
        return response
    }
    throw new Error(response?.error || 'Failed to cancel invitation')
}

/**
 * Get pending team invitations for an account
 * @param {string} accountID - Account ID
 * @returns {Promise<Object>} Returns { invitations: [], totalInvitations }
 */
export const getTeamInvitations = async (accountID) => {
    const action = {
        action: 'getTeamInvitations',
        accountID,
    }
    const response = await mobileApiRequest(action)
    if (response?.invitations) {
        return response
    }
    throw new Error(response?.error || 'Failed to get team invitations')
}
